import { useState } from 'react';
import {
  isMandatoryField,
  isValueValid,
  shouldAppearConditionally,
  shouldVerifyIfValid,
} from 'helpers/validation';
import { CsQuestion, CsQuestionWithOptions } from 'types/contentStack';
import { FormDataType } from './types';

type UseFormDataValidationReturnType = {
  validateFormData: (onSucces?: () => void, onError?: () => void) => void;
  getError: (fieldValue: string) => string | undefined;
  resetFieldError: (key: string) => void;
};

const useFormDataValidation = (
  fields: (CsQuestion | CsQuestionWithOptions)[],
  formData: FormDataType
): UseFormDataValidationReturnType => {
  const [fieldErrorsOnSubmit, setFieldErrorsOnSubmit] = useState<{
    [key: string]: string | undefined;
  }>({});

  const getError = (fieldValue: string): string | undefined =>
    fieldErrorsOnSubmit[fieldValue];

  const resetFieldError = (key: string): void => {
    if (!fieldErrorsOnSubmit[key]) {
      return;
    }

    setFieldErrorsOnSubmit((prevData) => ({
      ...prevData,
      [`${key}`]: undefined,
    }));
  };

  /* Checks whether a message for missing field / invalid value is set for each key.
   Unless set, it means that the field is optional and/or doesn't require regex validation. */
  const validateFormData = (onSucces?: () => void, onError?: () => void): void => {
    let errorFound = false;
    fields.forEach((field) => {
      // If the field's appearance depends on another field and that field doesn't have the requested value, skip validation since it doesn't exist.
      if (shouldAppearConditionally(field)) {
        const dependentFieldKey = field.show_if_field.value;
        if (!(formData[`${dependentFieldKey}`] === field.show_if_field.to_contain)) {
          return;
        }
      }
      const data = formData[field.value] as string;
      if (!data && isMandatoryField(field)) {
        setFieldErrorsOnSubmit((prevData) => ({
          ...prevData,
          [`${field.value}`]: field.error_messages?.missing,
        }));
        errorFound = true;
        return;
      }

      if (shouldVerifyIfValid(field)) {
        // Skip setting error message if nothing is set in the field or if there is and it passes regex validation
        if (!data || (!!data && isValueValid(data, field))) {
          return;
        }

        setFieldErrorsOnSubmit((prevData) => ({
          ...prevData,
          [`${field.value}`]: field.error_messages?.invalid_value,
        }));
        errorFound = true;
      }
    });

    if (errorFound) {
      onError?.();
    } else {
      onSucces?.();
    }
  };

  return { validateFormData, getError, resetFieldError };
};
export default useFormDataValidation;
